import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, ChevronDown, TriangleAlert } from "lucide-react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Textarea } from "../../ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "../../ui/toggle-group";
import { cn } from "../../utils/common/cn";
import { hasTier } from "./access-control";
import { useFacilities } from "../../utils/portfolio-risk-concentration/api/facilities";
import { ratePct, usd, usdM } from "./format";
import { INVESTOR_LABEL } from "./PrcFilterBar";
import {
  ADVANCE_RATE_TYPE,
  APPETITE_TYPE,
  CHANGE_KIND_LABEL,
  findLiveSource,
  MAPPABLE_TYPES,
  sameCodeSet,
  UNBOOKED_TYPES,
  useCapitalProposals,
  useCreateInvestor,
  useInvestorCodes,
  useInvestors,
  useReviewCapital,
  type CapitalChangeKind,
  type CapitalProposal,
  type CapitalProposalInput,
  type CapitalReviewResult,
  type CapitalSource,
  type CapitalType,
  type Investor,
} from "../../utils/portfolio-risk-concentration/api/capital";

/**
 * Add or adjust a capital source.
 *
 * ── One form, two shapes, and the fork is whether it is held in a book ──────────────────
 * Debt is, so it asks which book and what it is called, and it is the only type with an
 * ADVANCE RATE — an advance rate is a lender's haircut on pledged collateral, and equity and
 * internal caps have no lender. Equity and Internal are not held in a book, so they ask
 * neither facility nor rate — those fields are ABSENT rather than disabled, because a
 * greyed-out "Advance rate" beside an equity ceiling implies equity has one that merely
 * cannot be edited.
 *
 * ── Three facts, three proposals ────────────────────────────────────────────────────────
 * The form collects all three definitional properties at once — ceiling, advance rate, code
 * mapping — and raises a SEPARATE proposal for each one that actually changed. That is not
 * an implementation convenience; it is the governance model:
 *
 *   - a reviewer approving a code reassignment is taking a different decision from one
 *     approving a ceiling rise, and needs to see them apart;
 *   - the permanent log has to say which kind of change each entry was, rather than
 *     "capital source edited";
 *   - and they can be approved in either order, because prc-api resolves the live row at
 *     approval time rather than trusting the reference recorded when the proposal was raised.
 *
 * A CREATION is the one exception: a new facility's ceiling and rate go in ONE proposal,
 * because a source is born with both and splitting them would leave a newly-approved
 * facility with no advance rate for however long the second review takes.
 *
 * ── Only one of the three types moves a risk number ─────────────────────────────────────
 * Debt and Equity record where the money came from. Internal is the ceiling we set to
 * measure an investor's limits against, and approving one rewrites
 * prc_limit_config.total_capital_usd — so it is the only type that carries the warning
 * below. Equity carried it until sql/81_ split funding from appetite, when raising what an
 * investor put in stopped re-scoring their limits.
 *
 * ── Never a second, competing row for one pot of money ──────────────────────────────────
 * The dialog looks up what is already LIVE for the chosen (type, investor, book) and
 * switches itself to an ADJUSTMENT, pre-filled with today's figures. That is the whole of
 * the "recognise Equity already exists" requirement, and it works the same way for Debt —
 * picking CAP 1 when CAP 1 already has a ceiling is an adjustment too, for the same reason.
 *
 * This is a display guarantee, not the real one. prc-api derives the change type from what
 * it finds live at insert time and ignores whatever the form thought, and partial unique
 * indexes on prc_capital_source make a duplicate impossible at the storage layer. Three
 * layers because the failure mode — two live ceilings for one pot of money — is silent, and
 * every percentage in the product would be measured against whichever one sorted first.
 *
 * ── Unbooked types are singletons PER INVESTOR ──────────────────────────────────────────
 * The spec called equity a global singleton with no Investor field. That cannot hold once
 * two investors each hold capital, so the form asks which investor and never which facility.
 */


const NEW_BOOK = "__new__";

/**
 * The sentinel the Investor select uses for "+ A new investor…".
 *
 * Registering an investor is NOT proposal-governed, unlike everything else on this form, and
 * the asymmetry is deliberate: a proposal reviews a decision about an amount, and an
 * investor is a name that constrains nothing and moves no figure. What it enables — a
 * capital source — is governed, and that is where the review belongs. A reviewer approving
 * "Jaguar Trust exists" would have no number in front of them.
 */
const NEW_INVESTOR = "__new_investor__";

/**
 * No `Internal` option — there is no internal-capital concept to offer here. The two
 * existing `Internal`-type rows (`cs-wafra-equity`, `cs-navitas-internal`) still exist and
 * still drive `prc_limit_config.total_capital_usd` exactly as before; this dialog simply
 * cannot create a third one any more. Everything downstream (`isBooked`, `hasAdvanceRate`,
 * `hasCodeMap`, the appetite-ceiling warning banner) already branches on `form.capitalType`
 * and needs no change — those branches just become unreachable for the removed value.
 */
const TYPES: { value: CapitalType; blurb: string }[] = [
  { value: "Debt", blurb: "Money borrowed against a book. Supplies that book's committed ceiling on the Capacity page." },
  { value: "Equity", blurb: "Money the investor put in. Funding, not a limit — changing it moves no risk number." },
];

interface FormState {
  capitalType: CapitalType;
  investor: string;
  newInvestorName: string;
  bookChoice: string;
  newBookCode: string;
  newBookName: string;
  /** Raw field text, so the box can be cleared or half-typed. Parsed on submit. */
  ceiling: string;
  /** Same, and a percent: "85" means 85%, never 0.85. */
  advanceRate: string;
  /** Normalised code strings, e.g. ["10", "42"]. Picked, never typed. */
  investorCodes: string[];
  effectiveStartDate: Date;
  reason: string;
}

const emptyForm = (): FormState => ({
  capitalType: "Debt",
  investor: "WAFRA",
  newInvestorName: "",
  // the first facility the database lists, once the dialog is open (see the effect below)
  bookChoice: "",
  newBookCode: "",
  newBookName: "",
  ceiling: "",
  advanceRate: "",
  investorCodes: [],
  effectiveStartDate: new Date(),
  reason: "",
});

/** Which of the three fields the user has taken over, so a prefill never overwrites typing. */
const untouched = () => ({ ceiling: false, rate: false, codes: false });

export interface AddCapitalSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every LIVE source, so the dialog can recognise an adjustment before submitting. */
  sources: CapitalSource[];
  propose: (input: CapitalProposalInput) => Promise<unknown>;
  /** Opens straight into an adjustment of this source, from a row's "Adjust" action. */
  editSource?: CapitalSource | null;
  onSubmitted?: (message: string) => void;
  /** Bubbles a review's result up so the page can offer "Set Up Limits Now" — a durable,
   *  page-level prompt (see ManageCapitalPage.tsx), not something that should live inside a
   *  modal that's about to close. */
  onReviewApplied?: (result: CapitalReviewResult) => void;
}

/**
 * Display name for an investor, from the registry, falling back to the raw code. Duplicated
 * from ManageCapitalPage's own `investorLabel` useMemo because the approval queue and log
 * moved here are independent top-level components with their own data fetching, not closures
 * over the page's state.
 */
function buildInvestorLabel(investors: Investor[]): (code: string) => string {
  const byCode = new Map(investors.map((i) => [i.investorCode, i.displayName]));
  return (code: string) => byCode.get(code) ?? INVESTOR_LABEL[code as "WAFRA"] ?? code;
}

export function AddCapitalSourceDialog({
  open,
  onOpenChange,
  sources,
  propose,
  editSource,
  onSubmitted,
  onReviewApplied,
}: AddCapitalSourceDialogProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(untouched);
  // Separate from `error`/`toast` above: the queue's own inline confirmation, so
  // approving/rejecting a pending proposal from inside this dialog never closes it.
  const [queueToast, setQueueToast] = useState<string | null>(null);

  // Read directly rather than through props, unlike `propose`. These two are reference data
  // the dialog alone needs — the page has no use for the BankDwn code list — and threading
  // them through would make the page re-render on a warehouse scan it never reads.
  const investorsQ = useInvestors();
  const codesQ = useInvestorCodes();
  // The books a debt source can sit in: every facility Manage Capital knows (CAP1, CAP2 and any added since), plus the option to name a new one.
  const facilitiesQ = useFacilities();
  const KNOWN_BOOKS = facilitiesQ.data ?? [];
  const createInvestor = useCreateInvestor();
  const canAddInvestor = hasTier("contributor");

  const investors = investorsQ.data ?? [];
  const observedCodes = codesQ.data ?? [];

  // Reset on every open so a cancelled edit never leaks into the next new source.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTouched(untouched());
    if (editSource) {
      setForm({
        capitalType: editSource.capitalType,
        investor: editSource.investor,
        newInvestorName: "",
        bookChoice: editSource.facilityCode ?? "",
        newBookCode: "",
        newBookName: "",
        ceiling: String(editSource.ceilingDollars),
        advanceRate:
          editSource.advanceRatePct === null ? "" : String(editSource.advanceRatePct),
        investorCodes: editSource.investorCodes ?? [],
        effectiveStartDate: new Date(),
        reason: "",
      });
    } else {
      setForm({ ...emptyForm(), bookChoice: KNOWN_BOOKS[0]?.code ?? "" });
    }
  }, [open, editSource?.capitalSourceId]);

  // A new source starts on the first facility the database lists: when the list arrives after the dialog opened, it is picked then.
  useEffect(() => {
    if (!open || editSource || form.bookChoice || KNOWN_BOOKS.length === 0) return;
    setForm((f) => (f.bookChoice ? f : { ...f, bookChoice: KNOWN_BOOKS[0].code }));
  }, [open, editSource, form.bookChoice, KNOWN_BOOKS.length]);

  const isBooked = !UNBOOKED_TYPES.includes(form.capitalType);
  const isNewBook = isBooked && form.bookChoice === NEW_BOOK;
  const isNewInvestor = form.investor === NEW_INVESTOR;
  const facilityCode = !isBooked
      ? null
      : isNewBook
        ? form.newBookCode.trim().toUpperCase() || null
        : form.bookChoice;

  /** Debt only — see the header note on why this is absent rather than disabled. */
  const hasAdvanceRate = form.capitalType === ADVANCE_RATE_TYPE;
  /** Debt and Equity: an appetite ceiling is not a pool of contracts. */
  const hasCodeMap = MAPPABLE_TYPES.includes(form.capitalType);

  // What is already live for this exact selection. Recomputed as the user changes the type,
  // the investor or the book, so switching from "add CAP 1" to "add Equity" flips the whole
  // dialog from creation to adjustment mid-edit.
  const live = useMemo(
    () => findLiveSource(sources, form.investor, form.capitalType, facilityCode),
    [sources, form.investor, form.capitalType, facilityCode],
  );
  const isAdjustment = !!live;

  // Pre-fill from the live row the moment a selection turns out to be an adjustment — but
  // only for the fields that have not been typed into, or a selection change would overwrite
  // the value being entered on every keystroke.
  useEffect(() => {
    if (!open || editSource) return;
    setForm((f) => ({
      ...f,
      ceiling: touched.ceiling ? f.ceiling : live ? String(live.ceilingDollars) : "",
      advanceRate: touched.rate
        ? f.advanceRate
        : live && live.advanceRatePct !== null
          ? String(live.advanceRatePct)
          : "",
      investorCodes: touched.codes ? f.investorCodes : (live?.investorCodes ?? []),
    }));
  }, [open, live?.capitalSourceId, editSource, touched.ceiling, touched.rate, touched.codes]);

  const selectedServiced = useMemo(
    () =>
      form.investorCodes.filter(
        (c) => observedCodes.find((o) => o.code === c)?.ownedServiced === "Serviced",
      ),
    [form.investorCodes, observedCodes],
  );

  function investorLabel(code: string): string {
    return investors.find((i) => i.investorCode === code)?.displayName ?? code;
  }

  function toggleCode(code: string, on: boolean) {
    setTouched((t) => ({ ...t, codes: true }));
    setForm((f) => ({
      ...f,
      investorCodes: on
        ? [...f.investorCodes, code].sort((a, b) => Number(a) - Number(b))
        : f.investorCodes.filter((c) => c !== code),
    }));
  }

  /**
   * The proposals this submit will raise: one per fact that actually changed.
   *
   * Computed rather than assumed, so the footer can say how many reviews are being asked
   * for, and so "nothing has changed" is caught before a no-op adjustment reaches the queue
   * and someone has to review a "$1.7B → $1.7B" entry.
   */
  function plan(ceiling: number): { kind: CapitalChangeKind; input: CapitalProposalInput }[] {
    const shared = {
      investor: form.investor,
      capitalType: form.capitalType,
      facilityCode,
      facilityName: !isBooked
          ? null
          : isNewBook
            ? form.newBookName.trim()
            : (KNOWN_BOOKS.find((b) => b.code === facilityCode)?.name ?? live?.facilityName ?? editSource?.facilityName ?? null),
      effectiveStartDate: format(form.effectiveStartDate, "yyyy-MM-dd"),
      reason: form.reason.trim(),
    };
    const rate = form.advanceRate.trim() === "" ? null : Number(form.advanceRate);
    const out: { kind: CapitalChangeKind; input: CapitalProposalInput }[] = [];

    if (!live) {
      // A creation. Ceiling and rate travel together — see the header.
      out.push({
        kind: "CEILING",
        input: {
          ...shared,
          changeKind: "CEILING",
          newCeilingDollars: ceiling,
          newAdvanceRatePct: hasAdvanceRate ? rate : null,
        },
      });
      if (hasCodeMap && form.investorCodes.length > 0) {
        out.push({
          kind: "CODE_MAP",
          input: { ...shared, changeKind: "CODE_MAP", investorCodes: form.investorCodes },
        });
      }
      return out;
    }

    if (ceiling !== live.ceilingDollars) {
      out.push({
        kind: "CEILING",
        input: { ...shared, changeKind: "CEILING", newCeilingDollars: ceiling },
      });
    }
    if (hasAdvanceRate && rate !== null && rate !== live.advanceRatePct) {
      out.push({
        kind: "ADVANCE_RATE",
        input: { ...shared, changeKind: "ADVANCE_RATE", newAdvanceRatePct: rate },
      });
    }
    if (hasCodeMap && !sameCodeSet(form.investorCodes, live.investorCodes)) {
      out.push({
        kind: "CODE_MAP",
        input: { ...shared, changeKind: "CODE_MAP", investorCodes: form.investorCodes },
      });
    }
    return out;
  }

  async function submit() {
    setError(null);
    const ceiling = Number(form.ceiling.replace(/[, $]/g, "").trim());
    if (!form.ceiling.trim() || !Number.isFinite(ceiling) || ceiling <= 0) {
      setError("A committed ceiling above zero is required.");
      return;
    }
    if (hasAdvanceRate && form.advanceRate.trim() !== "") {
      const rate = Number(form.advanceRate);
      if (!Number.isFinite(rate) || rate <= 0 || rate > 100) {
        setError("The advance rate is a percent between 0 and 100.");
        return;
      }
      // Caught here as well as in prc-api because it is the one input on this form where a
      // wrong-by-100x value looks perfectly reasonable in the box.
      if (rate < 1) {
        setError(
          `An advance rate of ${rate}% is almost certainly meant as ${rate * 100}% — ` +
            "enter it as a percent, not a fraction.",
        );
        return;
      }
    }
    // Said out loud rather than silently ignored. An emptied box looks like "remove the
    // rate", and that is not a change the model has: null means NO RATE RECORDED, and
    // going from a rate a lender agreed back to "unknown" is a deletion of a fact, not a
    // decision about one. sql/83_'s shape CHECK makes it impossible anyway — an
    // ADVANCE_RATE proposal must carry a rate — so without this the field would just
    // appear not to work.
    if (
      hasAdvanceRate &&
      form.advanceRate.trim() === "" &&
      live &&
      live.advanceRatePct !== null
    ) {
      setError(
        `${labelFor(form.capitalType, facilityCode, investorLabel(form.investor))} has an ` +
          `advance rate of ${ratePct(live.advanceRatePct)}. It can be changed to another ` +
          "rate, but not cleared — an agreed rate does not become unknown again.",
      );
      return;
    }
    if (isBooked) {
      if (!facilityCode) {
        setError("A facility code is required for a debt source.");
        return;
      }
      if (isNewBook && !form.newBookName.trim()) {
        setError("A facility name is required for a new book.");
        return;
      }
    }
    if (isNewInvestor && !form.newInvestorName.trim()) {
      setError("A name is required for a new investor.");
      return;
    }
    if (!form.reason.trim()) {
      setError("A reason is required for every proposal.");
      return;
    }

    setSubmitting(true);
    // Named outside the try so the error path can say what DID land. A partial submit is
    // possible by construction — three independent proposals, three independent inserts —
    // and reporting "could not submit" over an already-raised ceiling change would send
    // someone to raise it twice.
    const raised: CapitalChangeKind[] = [];
    try {
      let investorCode = form.investor;
      if (isNewInvestor) {
        const created = await createInvestor.mutateAsync({
          displayName: form.newInvestorName.trim(),
        });
        investorCode = created.investorCode;
        // Adopt it immediately: a failure in the proposals below must not leave the select
        // sitting on "+ A new investor…" and re-register the same name on retry.
        setForm((f) => ({ ...f, investor: investorCode, newInvestorName: "" }));
      }

      const steps = plan(ceiling).map((s) => ({
        ...s,
        input: { ...s.input, investor: investorCode },
      }));
      if (steps.length === 0) {
        setError(
          "Nothing has changed. Adjust the ceiling, the advance rate or the mapped codes.",
        );
        return;
      }

      for (const step of steps) {
        await propose(step.input);
        raised.push(step.kind);
      }

      onOpenChange(false);
      const subject = labelFor(form.capitalType, facilityCode, investorLabel(investorCode));
      const what = raised.map((k) => CHANGE_KIND_LABEL[k].toLowerCase()).join(" and ");
      onSubmitted?.(
        isAdjustment
          ? `${subject}: ${what} proposed for review.`
          : `New ${form.capitalType.toLowerCase()} source ${subject} proposed — ${what} ` +
              `${raised.length > 1 ? "each await" : "awaits"} review.`,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not submit the proposal.";
      setError(
        raised.length
          ? `${raised
              .map((k) => CHANGE_KIND_LABEL[k])
              .join(" and ")} raised, but the rest failed: ${message}`
          : message,
      );
    } finally {
      setSubmitting(false);
    }
  }

  const pendingPlan = useMemo(() => {
    const ceiling = Number(form.ceiling.replace(/[, $]/g, "").trim());
    if (!Number.isFinite(ceiling) || ceiling <= 0) return [];
    return plan(ceiling).map((s) => s.kind);
    // Recomputed on every field the plan reads; `plan` closes over the whole form.
  }, [form, live, isBooked, isNewBook, hasAdvanceRate, hasCodeMap]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {isAdjustment ? "Adjust Capital Source" : "Add Capital Source"}
          </DialogTitle>
          <DialogDescription>
            {isAdjustment ? (
              <>
                {labelFor(form.capitalType, facilityCode, investorLabel(form.investor))} already
                has a live ceiling of <strong>{usd(live!.ceilingDollars)}</strong>, effective{" "}
                {live!.effectiveStartDate}. Each fact you change below — ceiling, advance rate,
                mapped codes — is proposed and reviewed separately, and the current row is
                closed out on approval so the history stays queryable.
              </>
            ) : (
              <>
                Nothing goes live until this is approved. A ceiling is the denominator every
                percentage limit is measured against, so it is governed exactly like a limit.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <Card className="border-border bg-transparent shadow-none">
        <CardContent className="space-y-4 pt-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          {/* Type first: it decides which of the fields below exist at all. Locked when
              adjusting an existing row — changing a source's type is not an adjustment, it
              is a different source. */}
          <div>
            <Label>Capital Type</Label>
            <ToggleGroup
              type="single"
              value={form.capitalType}
              onValueChange={(v) => {
                if (v) setForm((f) => ({ ...f, capitalType: v as CapitalType }));
              }}
              className="mt-1 h-9 justify-start gap-0 rounded-md border border-input bg-background p-0.5"
            >
              {TYPES.map((t) => (
                <ToggleGroupItem
                  key={t.value}
                  value={t.value}
                  disabled={!!editSource}
                  className={cn(
                    "h-8 min-w-[84px] rounded-[3px] px-3 text-sm font-medium",
                    "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                    "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground",
                    "data-[state=on]:hover:bg-primary",
                  )}
                >
                  {t.value}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {TYPES.find((t) => t.value === form.capitalType)?.blurb}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Investor</Label>
              <Select
                value={form.investor}
                disabled={!!editSource}
                onValueChange={(v) => setForm((f) => ({ ...f, investor: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {investors.map((i) => (
                    <SelectItem key={i.investorCode} value={i.investorCode}>
                      {i.displayName}
                    </SelectItem>
                  ))}
                  {/* Registering an investor is NOT proposal-governed -- it writes live
                      data through POST /investors, which require_tier gates at
                      contributor. Offering it to a reviewer would only earn them a 403
                      part-way through filling in this form. */}
                  {canAddInvestor && (
                    <SelectItem value={NEW_INVESTOR}>+ Add new investor…</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Absent for Equity, not disabled — see the header note. */}
            {isBooked && (
              <div>
                <Label>Facility</Label>
                <Select
                  value={form.bookChoice}
                  disabled={!!editSource}
                  onValueChange={(v) => setForm((f) => ({ ...f, bookChoice: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KNOWN_BOOKS.map((b) => (
                      <SelectItem key={b.code} value={b.code}>
                        {b.name}
                      </SelectItem>
                    ))}
                    {/* The facility of the row being adjusted, when it is not one of the known books (ABS Scenario's):
                        without an item for it the locked select rendered blank. */}
                    {editSource?.facilityCode && !KNOWN_BOOKS.some((b) => b.code === editSource.facilityCode) && (
                      <SelectItem value={editSource.facilityCode}>
                        {editSource.facilityName ?? editSource.facilityCode}
                      </SelectItem>
                    )}
                    <SelectItem value={NEW_BOOK}>+ A new facility…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {isNewInvestor && (
            <div>
              <Label htmlFor="cap-investor-name">Investor Name</Label>
              <Input
                id="cap-investor-name"
                className="mt-1"
                placeholder="e.g. Jaguar Trust"
                value={form.newInvestorName}
                onChange={(e) => setForm((f) => ({ ...f, newInvestorName: e.target.value }))}
              />
              {/* The code is DERIVED, not asked for. Two fields would drift — nothing would
                  stop 'JAGUAR' being called 'Jaguar Trust' and 'JAGUAR_TRUST' being called
                  'Jaguar' — and the code is what lands in prc_limit_config.investor for
                  every limit set under this investor from then on. */}
              <p className="mt-1 h-4 text-[11px] text-muted-foreground">
                {form.newInvestorName.trim()
                  ? `Registered as ${investorCodeFrom(form.newInvestorName)}. Created when you `
                    + "submit; the capital source itself still goes to review."
                  : ""}
              </p>
            </div>
          )}

          {isNewBook && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="cap-book-code">Facility Code</Label>
                <Input
                  id="cap-book-code"
                  className="mt-1"
                  placeholder="e.g. CAP3"
                  value={form.newBookCode}
                  onChange={(e) => setForm((f) => ({ ...f, newBookCode: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="cap-book-name">Facility Name</Label>
                <Input
                  id="cap-book-name"
                  className="mt-1"
                  placeholder="e.g. CAP 3 Back Book"
                  value={form.newBookName}
                  onChange={(e) => setForm((f) => ({ ...f, newBookName: e.target.value }))}
                />
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cap-ceiling">Committed Ceiling</Label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  $
                </span>
                <Input
                  id="cap-ceiling"
                  inputMode="decimal"
                  className="pl-6"
                  placeholder="1700000000"
                  value={form.ceiling}
                  onChange={(e) => {
                    setTouched((t) => ({ ...t, ceiling: true }));
                    setForm((f) => ({ ...f, ceiling: e.target.value }));
                  }}
                />
              </div>
              {/* Echoed back formatted, because a nine-digit number typed into a box is
                  almost impossible to read back. An order-of-magnitude slip here moves
                  every percentage in the product. */}
              <p className="mt-1 h-4 text-[11px] text-muted-foreground">
                {readableCeiling(form.ceiling)}
              </p>
            </div>

            <div>
              <Label>Effective From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="mt-1 w-full justify-start gap-2 font-normal">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {format(form.effectiveStartDate, "d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.effectiveStartDate}
                    onSelect={(d) => d && setForm((f) => ({ ...f, effectiveStartDate: d }))}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* ── The two definitional properties this form gained ──────────────────────────
              Both sit below the ceiling because they are the same category of fact: what
              this source IS, not what is allowed inside it. Neither exists for an Internal
              cap, so on that type this whole row is absent. */}
          {(hasAdvanceRate || hasCodeMap) && (
            <div className="grid gap-3 sm:grid-cols-2">
              {hasAdvanceRate && (
                <div>
                  <Label htmlFor="cap-advance-rate">Advance Rate</Label>
                  <div className="relative mt-1">
                    <Input
                      id="cap-advance-rate"
                      inputMode="decimal"
                      className="pr-7"
                      placeholder="85"
                      value={form.advanceRate}
                      onChange={(e) => {
                        setTouched((t) => ({ ...t, rate: true }));
                        setForm((f) => ({ ...f, advanceRate: e.target.value }));
                      }}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      %
                    </span>
                  </div>
                  {/* Says what the number MEANS, not just what it is. A rate is worth
                      nothing on its own — it is the dollars it turns collateral into that
                      anyone is deciding about. */}
                  <p className="mt-1 h-4 text-[11px] text-muted-foreground">
                    {readableRate(form.advanceRate, form.ceiling)}
                  </p>
                </div>
              )}

              {hasCodeMap && (
                <div>
                  <Label>Investor Codes</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="mt-1 w-full justify-between gap-2 font-normal"
                      >
                        <span className="truncate">
                          {form.investorCodes.length === 0
                            ? "None mapped"
                            : form.investorCodes.join(", ")}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-0">
                      {/* A fixed list, never free text. A mapping to a code the warehouse
                          has never seen maps nothing — silently, for ever, with no error
                          anywhere — so the pool would simply never contain any contracts.

                          ── Three states, and the third one is why this is not a ternary ──
                          A FAILED fetch used to render as "0 codes observed in BankDwn",
                          because `codesQ.data ?? []` makes an error and an empty warehouse
                          the same value. That is a claim about the book — and it was false:
                          the query behind it was broken while BankDwn held twenty codes. An
                          error has to say it could not read, never that it read nothing. */}
                      <div className="border-b border-border px-3 py-2">
                        {codesQ.isLoading ? (
                          <p className="text-[11px] text-muted-foreground">
                            Reading the investor listing…
                          </p>
                        ) : codesQ.isError ? (
                          <p className="text-[11px] text-destructive">
                            Could not read the investor codes from the investor listing. This is
                            a fetch failure, not an empty listing — mapping is unavailable until
                            it succeeds.
                          </p>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">
                            {observedCodes.length} live codes in the investor listing. Only these
                            can be mapped.
                          </p>
                        )}
                      </div>
                      <div className="max-h-64 space-y-0.5 overflow-y-auto p-1.5">
                        {/* The code, and nothing else. This row used to carry each code's
                            contract count and dollar value, and a "serviced · no exposure"
                            marker on the Serviced ones; both were removed on request.
                            items-center rather than items-start now that the row is always
                            one line -- the checkbox no longer needs nudging down to meet a
                            wrapping label.

                            Worth knowing if this is ever revisited: a Serviced code
                            contributes ZERO exposure to a pool by construction, because
                            label_owned_serviced is derived from the code itself and the
                            product filters to Owned. Mapping one is silently a no-op. The
                            observed-code data still carries ownedServiced, so the marker
                            can come back without any backend change. */}
                        {observedCodes.map((c) => {
                          const checked = form.investorCodes.includes(c.code);
                          return (
                            <label
                              key={c.code}
                              className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-muted/40"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(v) => toggleCode(c.code, !!v)}
                              />
                              <span className="font-medium">{c.code}</span>
                            </label>
                          );
                        })}
                        {!codesQ.isLoading && !codesQ.isError && observedCodes.length === 0 && (
                          <p className="px-1.5 py-3 text-center text-xs text-muted-foreground">
                            BankDwn carries no investor_code on any contract.
                          </p>
                        )}
                        {codesQ.isError && (
                          <p className="px-1.5 py-3 text-center text-xs text-muted-foreground">
                            {codesQ.error instanceof Error
                              ? codesQ.error.message
                              : "The request failed."}
                          </p>
                        )}
                      </div>
                      {form.investorCodes.length > 0 && (
                        <div className="border-t border-border p-1.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-full text-xs"
                            onClick={() => {
                              setTouched((t) => ({ ...t, codes: true }));
                              setForm((f) => ({ ...f, investorCodes: [] }));
                            }}
                          >
                            Clear all
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p className="mt-1 h-4 text-[11px] text-muted-foreground">
                    {form.investorCodes.length === 0
                      ? "Which raw codes constitute this pool"
                      : `${form.investorCodes.length} of ${observedCodes.length} codes`}
                  </p>
                </div>
              )}
            </div>
          )}

          {selectedServiced.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber-dim/30 px-3 py-2 text-[11px] text-muted-foreground">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber" />
              <span>
                Code {selectedServiced.join(", ")}{" "}
                {selectedServiced.length === 1 ? "is" : "are"} SERVICED, not owned. Every
                figure in this product is measured over{" "}
                <code className="text-[10px]">consider AND Owned</code>, so mapping{" "}
                {selectedServiced.length === 1 ? "it" : "them"} adds no exposure to this pool
                at all.
              </span>
            </div>
          )}

          {/* The one warning worth interrupting for: an internal-cap change rebases live
              risk numbers, which a ceiling on a book does not. Equity used to carry this
              and no longer does — since sql/81_ split funding from appetite, raising what
              an investor put in re-scores nothing. */}
          {form.capitalType === APPETITE_TYPE && (
            <div className="rounded-md border border-amber/40 bg-amber-dim/30 px-3 py-2 text-[11px] text-muted-foreground">
              On approval this becomes the capital base for every{" "}
              {investorLabel(form.investor)} internal limit. Their percentages, headrooms and
              RAG statuses are recomputed against it.
            </div>
          )}

          <div>
            <Label htmlFor="cap-reason">Reason</Label>
            <Textarea
              id="cap-reason"
              className="mt-1"
              rows={3}
              placeholder="Why is this ceiling being set or changed?"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
            {/* Stated before submitting rather than discovered in the queue afterwards: two
                changes mean two reviews, and a reviewer may approve them in either order. */}
            {pendingPlan.length > 1 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                This raises {pendingPlan.length} separate proposals —{" "}
                {pendingPlan.map((k) => CHANGE_KIND_LABEL[k].toLowerCase()).join(", ")} — each
                reviewed and logged on its own.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={submitting} onClick={submit}>
              {submitting
                ? "Submitting…"
                : isAdjustment
                  ? pendingPlan.length > 1
                    ? `Propose ${pendingPlan.length} Changes`
                    : "Propose Adjustment"
                  : "Propose Source"}
            </Button>
          </div>
        </CardContent>
        </Card>

        {queueToast && (
          <div className="rounded-md border border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 px-3 py-2 text-xs">
            {queueToast}
          </div>
        )}
        <CapitalApprovalQueue onDecided={setQueueToast} onReviewApplied={onReviewApplied} />
        <CapitalLog />
      </PrcDialogContent>
    </Dialog>
  );
}

function labelFor(
  capitalType: CapitalType,
  facilityCode: string | null,
  investor: string,
): string {
  if (capitalType === "Debt") return facilityCode ?? "this facility";
  return `${investor} ${capitalType.toLowerCase()}${
    capitalType === APPETITE_TYPE ? " cap" : ""
  }`;
}

/**
 * "Jaguar Trust" -> "JAGUAR_TRUST". Mirrors capital.py::_investor_code_from, and is shown
 * to the user before they submit so the identifier they are creating is not a surprise.
 */
function investorCodeFrom(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
}

/** "$1,700,000,000 · 1,700.0M" for a typed ceiling, or nothing while it is unusable. */
function readableCeiling(raw: string): string {
  const n = Number(raw.replace(/[, $]/g, "").trim());
  if (!raw.trim() || !Number.isFinite(n) || n <= 0) return "";
  return `${usd(n)} · ${(n / 1_000_000).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}M`;
}

/**
 * What an advance rate is worth against the ceiling being set.
 *
 * NOT a capacity figure, and worded to avoid reading as one: real usable capacity is the
 * rate applied to ELIGIBLE NET INVESTMENT pledged, which is a live warehouse number this
 * form has no business computing. "85% of $1.7B" is the arithmetic the reviewer is
 * approving, and it is enough to catch a slipped decimal.
 */
function readableRate(rate: string, ceiling: string): string {
  const r = Number(rate.trim());
  if (!rate.trim() || !Number.isFinite(r) || r <= 0 || r > 100) return "";
  const c = Number(ceiling.replace(/[, $]/g, "").trim());
  if (!Number.isFinite(c) || c <= 0) return `${ratePct(r)} of pledged collateral`;
  return `${ratePct(r)} — ${usdM((c * r) / 100)} against this ceiling`;
}

/**
 * What a proposal is ABOUT — a book for debt, an investor's pot for the rest.
 *
 * `label` is threaded through rather than reaching for INVESTOR_LABEL directly so a source
 * registered under a newly-added investor reads the same here as it does in the table.
 * INVESTOR_LABEL is the fallback, not the source of truth: it is a fixed map of the two
 * investors that predate "+ Add new investor".
 */
function sourceLabel(
  p: { capitalType: string; facilityName: string | null; facilityCode: string | null; investor: string },
  label: (code: string) => string = (c) => INVESTOR_LABEL[c as "WAFRA"] ?? c,
) {
  return p.capitalType === "Debt"
    ? (p.facilityName ?? p.facilityCode ?? "Capital source")
    : `${label(p.investor)} ${p.capitalType.toLowerCase()}${
        p.capitalType === "Internal" ? " cap" : ""
      }`;
}

/**
 * What a proposal changes, as one "before → after" string, in the units of its own kind.
 *
 * One function rather than three call sites choosing a formatter, because the units differ
 * and mixing them up is the failure that matters: a rate rendered with `usd` reads "$85",
 * and a ceiling rendered with `ratePct` reads "1700000000%". The kind is the only thing that
 * says which is which.
 */
function changeSummary(p: CapitalProposal): string {
  switch (p.changeKind) {
    case "ADVANCE_RATE":
      return `${ratePct(p.oldAdvanceRatePct)} → ${ratePct(p.newAdvanceRatePct)}`;
    case "CODE_MAP":
      return `${codeList(p.oldInvestorCodes)} → ${codeList(p.newInvestorCodes)}`;
    default: {
      // A creation reads "— → $385,000,000"; an adjustment "$2.5B → $2.75B". A creation that
      // also set an advance rate says so, because that is what one proposal decided.
      const ceiling = `${p.oldCeilingDollars === null ? "—" : usd(p.oldCeilingDollars)} → ${usd(
        p.newCeilingDollars,
      )}`;
      return p.newAdvanceRatePct === null
        ? ceiling
        : `${ceiling} · advance rate ${ratePct(p.newAdvanceRatePct)}`;
    }
  }
}

/** "10, 42" — or "none" for an approved-empty mapping, "—" for no mapping at all. */
function codeList(codes: string[] | null): string {
  if (codes === null) return "—";
  return codes.length === 0 ? "none" : codes.join(", ");
}

/**
 * The approval queue. Same shape and the same mandatory-notes rule as Manage limits'
 * — a reviewer moving between the two queues should not have to learn a second interaction.
 *
 * Self-contained (own `useCapitalProposals`/`useReviewCapital`/`useInvestors`), matching
 * `FairValueApprovalQueue` in ContractValuationSettings.tsx — this lives inside the Add/
 * Adjust dialog rather than being threaded down from the page.
 */
function CapitalApprovalQueue({
  onDecided,
  onReviewApplied,
}: {
  onDecided: (message: string) => void;
  onReviewApplied?: (result: CapitalReviewResult) => void;
}) {
  const proposalsQ = useCapitalProposals(undefined, { limit: 100, offset: 0 });
  const pending = useMemo(
    () => (proposalsQ.data?.proposals ?? []).filter((p) => p.status === "PENDING"),
    [proposalsQ.data],
  );
  const review = useReviewCapital();
  const canDecide = hasTier("contributor");
  const investorsQ = useInvestors();
  const investorLabelFn = useMemo(
    () => buildInvestorLabel(investorsQ.data ?? []),
    [investorsQ.data],
  );

  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function act(p: CapitalProposal, decision: "APPROVED" | "REJECTED") {
    const note = (notes[p.proposalId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [p.proposalId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(p.proposalId);
      const res = await review.mutateAsync({ proposalId: p.proposalId, decision, notes: note });
      if (!res.ok) throw new Error(res.error ?? "Review failed.");
      const what = `${sourceLabel(p, investorLabelFn)} ${CHANGE_KIND_LABEL[
        p.changeKind
      ].toLowerCase()}`;
      onDecided(
        decision === "APPROVED"
          ? `${what} approved and effective ${p.effectiveStartDate}.`
          : `${what} rejected.`,
      );
      if (res.needsLimits) onReviewApplied?.(res);
      setNotes((n) => ({ ...n, [p.proposalId]: "" }));
      setErrors((e) => ({ ...e, [p.proposalId]: "" }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [p.proposalId]: err instanceof Error ? err.message : "Review failed.",
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          Approval Queue{pending.length ? ` (${pending.length})` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pending.length === 0 ? (
          <p className="text-sm text-muted-foreground">No proposals awaiting review.</p>
        ) : (
          <div className="space-y-4">
            {pending.map((p) => (
              <div key={p.proposalId} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 text-sm">
                    {/* The KIND leads, because it is what the reviewer is being asked to
                        decide. "Adjust debt capital — CAP1" describes three different
                        decisions; "Advance rate — CAP1" describes one. */}
                    <p className="font-medium">
                      {CHANGE_KIND_LABEL[p.changeKind]} —{" "}
                      {p.changeType === "ADD" ? "set for" : "change to"}{" "}
                      {sourceLabel(p, investorLabelFn)}{" "}
                      <span className="text-muted-foreground">
                        ({p.capitalType.toLowerCase()})
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {changeSummary(p)} · effective {p.effectiveStartDate} · proposed by{" "}
                      {p.proposedByRole}
                    </p>
                    {p.reason && <p className="mt-1 text-xs">{p.reason}</p>}
                    {/* The consequence, stated where the decision is made rather than
                        discovered afterwards on Monitor.
                        INTERNAL, not Equity: sql/81_ split funding from appetite, and this
                        warning followed the amount that actually rewrites
                        prc_limit_config.total_capital_usd. It sat on Equity until then,
                        which meant it warned about the one type that re-scores nothing and
                        stayed silent on the one that re-scores everything. */}
                    {p.capitalType === "Internal" && p.changeKind === "CEILING" && (
                      <p className="mt-1 text-[11px] text-amber">
                        Approving rebases every {p.investor} internal limit onto this capital
                        base — their percentages, headrooms and RAG statuses all move with it.
                      </p>
                    )}
                    {p.changeKind === "CODE_MAP" && (
                      <p className="mt-1 text-[11px] text-amber">
                        This changes which raw BankDwn contracts constitute the pool, not how
                        much capital it holds.
                      </p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                  >
                    Pending
                  </Badge>
                </div>

                {/* Deciding is the contributor's half of maker-checker. The notes box
                    goes with the buttons, since it feeds them. Backed by require_tier on
                    POST /capital-proposals/{id}/review. */}
                {canDecide && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      rows={2}
                      placeholder="Review notes (required)"
                      value={notes[p.proposalId] ?? ""}
                      onChange={(e) =>
                        setNotes((n) => ({ ...n, [p.proposalId]: e.target.value }))
                      }
                    />
                    {errors[p.proposalId] && (
                      <p className="text-xs text-destructive">{errors[p.proposalId]}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "APPROVED")}
                      >
                        {busy === p.proposalId ? "Applying…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The permanent log. Newest first, nothing deleted — rejections stay too.
 *
 * ── Why there is a "Change" column and not a "Ceiling" one ──────────────────────────────
 * Three kinds of change land here, in three different units — dollars, a percent, a set of
 * codes — so a column headed "Ceiling" could only be right for one of them. The Kind column
 * says which decision each row was, and Change renders it in that kind's own units. Without
 * the pair, an advance-rate entry reading "— → 85" under a Ceiling header is indistinguishable
 * from a facility committed to eighty-five dollars.
 *
 * Table hardened with `table-fixed` + percentage column widths, same recipe as
 * ContractValuationSettings.tsx's FairValueLog — this lives inside a dialog now, and a fixed
 * layout is what keeps a long Reason string from forcing horizontal scroll.
 */
function CapitalLog() {
  const proposalsQ = useCapitalProposals(undefined, { limit: 100, offset: 0 });
  const proposals = proposalsQ.data?.proposals ?? [];
  const investorsQ = useInvestors();
  const investorLabelFn = useMemo(
    () => buildInvestorLabel(investorsQ.data ?? []),
    [investorsQ.data],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Capital Change Log</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-hidden rounded-lg border border-border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[7%]">Status</TableHead>
                <TableHead className="w-[9%]">Kind</TableHead>
                <TableHead className="w-[8%]">Investor</TableHead>
                <TableHead className="w-[10%]">Facility</TableHead>
                <TableHead className="w-[7%]">Type</TableHead>
                <TableHead className="w-[14%]">Change</TableHead>
                <TableHead className="w-[9%]">Effective From</TableHead>
                <TableHead className="w-[9%]">Proposed By</TableHead>
                <TableHead className="w-[15%]">Reason</TableHead>
                <TableHead className="w-[12%]">Reviewed By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="py-6 text-center text-sm text-muted-foreground">
                    Nothing proposed yet.
                  </TableCell>
                </TableRow>
              )}
              {proposals.map((p) => (
                <TableRow key={p.proposalId}>
                  <TableCell className="px-2 py-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        "whitespace-nowrap text-[10px] uppercase tracking-[0.1em]",
                        p.status === "APPROVED"
                          ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
                          : p.status === "REJECTED"
                            ? "border-destructive/50 text-destructive"
                            : "border-primary/50 text-primary",
                      )}
                    >
                      {/* An approved capital change is what put a row LIVE, so the log reads
                          in the vocabulary of the record it created rather than of the
                          paperwork that created it. */}
                      {p.status === "APPROVED" ? "Live" : p.status}
                    </Badge>
                  </TableCell>
                  {/* Which of the three facts this entry was about. Never inferred from the
                      populated columns — a first advance rate and a first mapping are both
                      "ADD" with a null "old", and only the kind tells them apart. */}
                  <TableCell className="px-2 py-2">
                    <Badge
                      variant="outline"
                      className="whitespace-nowrap border-border text-[10px] uppercase tracking-[0.1em] text-muted-foreground"
                    >
                      {CHANGE_KIND_LABEL[p.changeKind]}
                    </Badge>
                  </TableCell>
                  <TableCell className="break-words px-2 py-2">
                    {investorLabelFn(p.investor)}
                  </TableCell>
                  <TableCell className="break-words px-2 py-2 text-muted-foreground">
                    {p.facilityName ?? p.facilityCode ?? "—"}
                  </TableCell>
                  <TableCell className="px-2 py-2">{p.capitalType}</TableCell>
                  <TableCell className="break-words px-2 py-2">{changeSummary(p)}</TableCell>
                  <TableCell className="px-2 py-2 text-muted-foreground">
                    {p.effectiveStartDate}
                  </TableCell>
                  <TableCell className="break-words px-2 py-2 text-muted-foreground">
                    {p.proposedByRole}
                  </TableCell>
                  <TableCell className="break-words px-2 py-2 text-xs">{p.reason}</TableCell>
                  <TableCell className="break-words px-2 py-2 text-xs text-muted-foreground">
                    {p.reviewedByRole ? (
                      <>
                        {p.reviewedByRole}
                        {p.reviewNotes && <div className="text-[11px]">{p.reviewNotes}</div>}
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
